import { push } from "connected-react-router";
import { fork, put, takeEvery, takeLatest } from "redux-saga/effects";

import { DEFAULT_PAGINATION } from "../../../constants/defaultPagination";
import { callApi } from "../../../sagas/helpers/api";
import { setError, setSuccess, startLoading } from "../../../store/appSlice";
import * as BanksApi from "../api/Banks";
import {
  banksOperationPath,
  fetchBank,
  fetchBanks,
  setBankActiveFlag,
  setBankBetaFlag,
  setItem,
  setPaginatedList,
  toggleVoltInstantPaymentsFlag,
  UpdateActionPayload,
  updateBank,
  updateItem,
} from ".";

import type { PaginatedList } from "../../../api/helpers/pagination";
import type { UUID } from "../../../utils";
import type { Bank } from "../models/Bank";

function* fetchBanksSaga(action: any) {
  const operationPath = ["banks", "list"];

  yield put(startLoading(operationPath));
  try {
    const {
      page = 0,
      limit = DEFAULT_PAGINATION.limit,
      order,
      orderBy,
      search,
    } = action.payload ?? {};

    const data: PaginatedList<Bank> = yield callApi(BanksApi.getBanks, {
      page,
      limit,
      order,
      orderBy,
      search,
    });

    yield put(setPaginatedList(data));

    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchBankSaga({
  payload,
}: {
  payload: {
    id: string;
  };
  type: string;
}) {
  const { id } = payload;
  const operationPath = ["banks", "items", id];

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const item = yield callApi(BanksApi.getBank, id);

    yield put(setItem({ id, item }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateBankSaga({
  payload,
}: {
  payload: UpdateActionPayload<Bank>;
  type: string;
}) {
  const { id, data, redirectOnSuccess = false, resolve } = payload;

  const operationPath = ["banks", "items", id];

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const bank = yield callApi(BanksApi.patchBank, id, data);

    yield put(setItem({ id, item: bank }));
    yield put(setSuccess(operationPath));

    if (redirectOnSuccess) {
      yield put(push(`/administration/banks/${bank.id}`));
    }

    if (resolve) {
      resolve();
    }
  } catch (error) {
    if (resolve) {
      resolve();
    }

    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
    yield put(
      setError({
        path: ["banks", "items"],
        error,
      })
    );
  }
}

function* setBankActiveFlagSaga({
  payload,
}: {
  payload: { id: string; value: boolean };
  type: string;
}) {
  const { id, value } = payload;
  const operationPath = ["bank", "items", id, "setBankActiveFlag"];

  yield put(startLoading(operationPath));
  try {
    // @ts-ignore noImplicitAny
    const bank = yield callApi(BanksApi.patchBank, id, {
      active: value,
    } as Bank);

    yield put(updateItem({ id, itemPart: { active: bank.active } }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* setBankBetaFlagSaga({
  payload,
}: {
  payload: { id: UUID; value: boolean };
  type: string;
}) {
  const { id, value } = payload;
  const operationPath = ["bank", "items", id, "setBankBetaFlag"];

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const bank = yield callApi(BanksApi.patchBank, id, {
      beta: value,
    } as Bank);

    yield put(updateItem({ id, itemPart: { beta: bank.beta } }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* toggleVoltInstantPaymentsFlagSaga({
  payload,
}: {
  payload: { id: UUID; value: boolean };
  type: string;
}) {
  const { id, value } = payload;
  const operationPath = banksOperationPath.voltInstantPayments(id);

  yield put(startLoading(operationPath));

  try {
    const bank: Bank = yield callApi(BanksApi.patchBank, id, {
      verifiedByVoltInstant: value,
    });

    yield put(
      updateItem({
        id,
        itemPart: { verifiedByVoltInstant: bank.verifiedByVoltInstant },
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchBanks() {
  yield takeLatest(fetchBanks.type, fetchBanksSaga);
}

function* watchFetchBank() {
  yield takeLatest(fetchBank.type, fetchBankSaga);
}

function* watchUpdateBank() {
  yield takeLatest(updateBank.type, updateBankSaga);
}

function* watchBankActiveFlag() {
  yield takeEvery(setBankActiveFlag.type, setBankActiveFlagSaga);
}

function* watchBankBetaFlag() {
  yield takeEvery(setBankBetaFlag.type, setBankBetaFlagSaga);
}

export default function* rootSaga() {
  yield fork(watchFetchBanks);
  yield fork(watchFetchBank);
  yield fork(watchBankActiveFlag);
  yield fork(watchBankBetaFlag);
  yield fork(watchUpdateBank);
  yield takeEvery(
    toggleVoltInstantPaymentsFlag.type,
    toggleVoltInstantPaymentsFlagSaga
  );
}
